import "./bulk-tender-modal.css";

import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { useCallback, useEffect, useRef, useState } from "react";

import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import type { ApiResult } from "~/shared/types/api-types.ts";

import { useBulkTender } from "../../hooks/use-bulk-tender.ts";
import type { BulkTenderDocumentFormData } from "../../schemas/bulk-tender-document-schema.ts";
import type {
	BulkTenderNotificationTarget,
	BulkTenderResponse,
} from "../../schemas/bulk-tender-schema.ts";
import type { BulkTenderPreflightResult } from "../../utils/bulk-tender-preflight.ts";
import { getBulkTenderLimitError } from "../../utils/bulk-tender-preflight.ts";
import { BulkTenderSuccessModal } from "./bulk-tender-success-modal.tsx";

type BulkTenderModalProps = {
	quoteIds: number[];
	onClose: () => void;
	onComplete: (tenderedQuoteIds: number[]) => void;
	onDownloadDocuments?: (formData: BulkTenderDocumentFormData) => void;
	onPreflight?: () => BulkTenderPreflightResult;
	onResultAction?: (quoteId: number, reasonCode: string) => void;
	onSendDocuments?: (formData: BulkTenderDocumentFormData) => void;
	onSubscribeToNotifications?: () => void;
	notificationTargets?: readonly BulkTenderNotificationTarget[];
};

export const BulkTenderModal = ({
	quoteIds,
	onClose,
	onComplete,
	onDownloadDocuments,
	onPreflight,
	onResultAction,
	onSendDocuments,
	onSubscribeToNotifications,
	notificationTargets,
}: BulkTenderModalProps) => {
	const [preflightError, setPreflightError] = useState<string | null>(null);
	const [submittedResult, setSubmittedResult] = useState<
		ApiResult<BulkTenderResponse> | undefined
	>();
	const bulkTenderMutation = useBulkTender();
	const { mutateAsync, reset } = bulkTenderMutation;
	const submissionError = bulkTenderMutation.error?.message;
	const result = submittedResult ?? bulkTenderMutation.data;
	const resultError = result && !result.success ? result.error : undefined;
	const errorMessage = submissionError ?? resultError;
	const hasSubmittedRef = useRef(false);

	const handleSubmit = useCallback(() => {
		const preflightResult = onPreflight?.() ?? {
			success: true as const,
			quoteIds: [...new Set(quoteIds)].sort((left, right) => left - right),
		};
		if (!preflightResult.success) {
			setPreflightError(preflightResult.error);
			return;
		}
		const limitError = getBulkTenderLimitError(preflightResult.quoteIds.length);
		if (limitError) {
			setPreflightError(limitError);
			return;
		}

		setPreflightError(null);
		void mutateAsync({ quoteIds: preflightResult.quoteIds }).then(
			(nextResult) => setSubmittedResult(nextResult),
			() => undefined,
		);
	}, [mutateAsync, onPreflight, quoteIds]);

	useEffect(() => {
		if (hasSubmittedRef.current) return;
		hasSubmittedRef.current = true;
		handleSubmit();
	}, [handleSubmit]);

	const handleRetry = () => {
		reset();
		setSubmittedResult(undefined);
		hasSubmittedRef.current = true;
		handleSubmit();
	};

	if (preflightError) {
		return (
			<TraxModal
				cancelText={"Close"}
				confirmText={"Try Again"}
				onCancel={onClose}
				onClose={onClose}
				onConfirm={handleRetry}
				showConfirmationButtons={true}
				title={"Unable to Tender Shipments"}
				type={"error"}
				width={584}
			>
				<p role={"alert"}>{preflightError}</p>
			</TraxModal>
		);
	}

	if (errorMessage) {
		return (
			<TraxModal
				cancelText={"Close"}
				className={"bulk-tender-modal"}
				confirmText={"Try Again"}
				onCancel={onClose}
				onClose={onClose}
				onConfirm={handleRetry}
				showConfirmationButtons={true}
				title={"Unable to Tender Shipments"}
				type={"error"}
				width={584}
			>
				<p>{errorMessage}</p>
			</TraxModal>
		);
	}

	if (!result) {
		return (
			<TraxModal
				cancelText={"Cancel"}
				className={"bulk-tender-modal"}
				confirmDisabled={true}
				confirmText={"Get Documents"}
				onCancel={onClose}
				onClose={onClose}
				onConfirm={() => undefined}
				showConfirmationButtons={true}
				title={"Tendering Shipments"}
				type={"primary"}
				width={584}
			>
				<div
					aria-live={"polite"}
					className={"bulk-tender-progress"}
					role={"status"}
				>
					<span aria-hidden={"true"} className={"bulk-tender-progress-loader"}>
						<Loader size={"small"} type={"infinite-spinner"} />
					</span>
					<div>Tendering {quoteIds.length} selected shipments...</div>
				</div>
			</TraxModal>
		);
	}

	if (!result.success) return null;

	return (
		<BulkTenderSuccessModal
			notificationTargets={notificationTargets}
			onComplete={() =>
				onComplete(
					result.data.results
						.filter((item) => item.status === "tendered")
						.map((item) => item.quoteId),
				)
			}
			onDownloadDocuments={onDownloadDocuments}
			onResultAction={onResultAction}
			onSendDocuments={onSendDocuments}
			onSubscribeToNotifications={onSubscribeToNotifications}
			response={result.data}
		/>
	);
};
